import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getMaintenanceNotice } from './api';
import { MaintenanceNotice as MaintenanceNoticeData } from './types';

const CACHE_KEY = 'athena_maintenance_notice_cache';
const DISMISSED_PREFIX = 'athena_maintenance_notice_dismissed:';
const CACHE_TTL_MS = 15 * 60 * 1000;

interface CachedNotice {
  cachedAt: number;
  notice: MaintenanceNoticeData;
}

interface MaintenanceNoticeProps {
  topOffset?: number | string;
}

const getNoticeKey = (notice: MaintenanceNoticeData) =>
  `${notice.updatedAt || ''}:${notice.title || ''}:${notice.message || ''}`;

const isNoticeCurrent = (notice: MaintenanceNoticeData, now = Date.now()) => {
  if (!notice.active || !notice.message?.trim()) return false;
  if (notice.startsAt && new Date(notice.startsAt).getTime() > now) return false;
  if (notice.endsAt && new Date(notice.endsAt).getTime() < now) return false;
  return true;
};

const getCachedNotice = (): MaintenanceNoticeData | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const cached = JSON.parse(raw) as CachedNotice;
    if (!cached?.notice || Date.now() - cached.cachedAt > CACHE_TTL_MS) return null;
    if (!isNoticeCurrent(cached.notice)) return null;

    return cached.notice;
  } catch {
    return null;
  }
};

const cacheNotice = (notice: MaintenanceNoticeData) => {
  try {
    if (isNoticeCurrent(notice)) {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ cachedAt: Date.now(), notice }));
    } else {
      localStorage.removeItem(CACHE_KEY);
    }
  } catch {}
};

const getAlertSeverity = (severity: MaintenanceNoticeData['severity']) => {
  if (severity === 'critical') return 'error';
  return severity;
};

const getTapeColors = (severity: MaintenanceNoticeData['severity']) => {
  if (severity === 'critical') return { bg: '#8B1538', color: '#fff' };
  if (severity === 'info') return { bg: '#0b5cad', color: '#fff' };
  return { bg: '#f57c00', color: '#1f1f1f' };
};

const MaintenanceNotice: React.FC<MaintenanceNoticeProps> = ({ topOffset = 64 }) => {
  const [notice, setNotice] = React.useState<MaintenanceNoticeData | null>(() => getCachedNotice());
  const [closedNoticeKey, setClosedNoticeKey] = React.useState<string | null>(null);

  const refreshNotice = React.useCallback(async () => {
    try {
      const nextNotice = await getMaintenanceNotice();
      if (isNoticeCurrent(nextNotice)) {
        setNotice(nextNotice);
        cacheNotice(nextNotice);
      } else {
        setNotice(null);
        cacheNotice(nextNotice);
      }
    } catch {
      const cached = getCachedNotice();
      if (cached) setNotice(cached);
    }
  }, []);

  React.useEffect(() => {
    refreshNotice();
    const interval = window.setInterval(refreshNotice, 60000);
    return () => window.clearInterval(interval);
  }, [refreshNotice]);

  if (!notice || !isNoticeCurrent(notice)) return null;

  const noticeKey = getNoticeKey(notice);
  if (closedNoticeKey === noticeKey) return null;

  try {
    if (notice.dismissible && localStorage.getItem(`${DISMISSED_PREFIX}${noticeKey}`) === 'true') {
      return null;
    }
  } catch {}

  const closeNotice = () => {
    if (notice.dismissible) {
      try { localStorage.setItem(`${DISMISSED_PREFIX}${noticeKey}`, 'true'); } catch {}
    }
    setClosedNoticeKey(noticeKey);
  };

  if (notice.displayType === 'popup') {
    return (
      <Dialog open onClose={closeNotice} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color={getAlertSeverity(notice.severity) as any} />
          {notice.title || 'Underhållsmeddelande'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {notice.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={closeNotice}>
            Stäng
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (notice.displayType === 'banner') {
    return (
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          top: topOffset,
          left: 16,
          right: 16,
          zIndex: (theme) => theme.zIndex.appBar + 2,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Alert
          severity={getAlertSeverity(notice.severity) as any}
          onClose={notice.dismissible ? closeNotice : undefined}
          sx={{ alignItems: 'center' }}
        >
          {notice.title && <strong>{notice.title}: </strong>}
          <span>{notice.message}</span>
        </Alert>
      </Paper>
    );
  }

  const colors = getTapeColors(notice.severity);
  const noticeText = `${notice.title ? `${notice.title}: ` : ''}${notice.message}`;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: topOffset,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar + 2,
        minHeight: 32,
        px: 2,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        bgcolor: colors.bg,
        color: colors.color,
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'inline-flex',
            minWidth: '200%',
            whiteSpace: 'nowrap',
            animation: 'maintenanceTapeMarquee 24s linear infinite',
            '@keyframes maintenanceTapeMarquee': {
              '0%': { transform: 'translateX(0)' },
              '100%': { transform: 'translateX(-50%)' },
            },
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              transform: 'none',
            },
          }}
        >
          {[0, 1].map((index) => (
            <Typography
              key={index}
              variant="body2"
              component="span"
              sx={{
                width: '50%',
                px: 6,
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              {noticeText}
            </Typography>
          ))}
        </Box>
      </Box>
      {notice.dismissible && (
        <IconButton size="small" onClick={closeNotice} sx={{ color: colors.color, flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export default MaintenanceNotice;
